/**
 * AdminBundleEdit — pełnostronicowa edycja pakietu (zastępuje modal edycji).
 *
 * Lewa kolumna: formularz (nazwa, rabat, wybór modułów). Prawa: bogaty „Skład
 * pakietu" — każdy moduł z nazwą + ceną mies/rok + flagi (wycofany / brak ceny),
 * podsumowanie: suma składników, rabat, cena pakietu i oszczędność (mies + rok).
 *
 * Cena pakietu = admin preview z `module_pricing` (faktyczna cena tenanta zamrażana
 * w F4 — ADR-0012). Save: PATCH /admin/bundles/:code (diff name/moduleCodes/discount).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { ArrowLeft, Check, Package } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Input } from '@/components/Input';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface BundleDetail {
  id: string;
  code: string;
  name: string | null;
  moduleCodes: string[];
  discountPercent: number;
  isActive: boolean;
  gcrecord: string | null;
}

interface ModuleListItem {
  code: string;
  name: string;
  isActive: boolean;
  gcrecord: string | null;
}

interface ModulePricingRow {
  moduleCode: string;
  currencyCode: string;
  priceMonthly: number;
  priceYearly: number | null;
}

function formatPln(grosze: number | null): string {
  if (grosze === null) return '—';
  return (grosze / 100).toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function AdminBundleEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { code = '' } = useParams<{ code: string }>();

  const bundleQuery = useQuery({
    queryKey: ['admin', 'bundles', 'detail', code],
    queryFn: async (): Promise<BundleDetail> => {
      const res = await api.get<{ bundle: BundleDetail }>(
        `/admin/bundles/${code}?include_deleted=true`,
      );
      return res.data.bundle;
    },
    enabled: Boolean(code),
    retry: false,
  });

  const modulesQuery = useQuery({
    queryKey: ['admin', 'modules', 'list-for-bundle'],
    queryFn: async () => {
      const res = await api.get<{ modules: ModuleListItem[] }>(
        '/admin/modules?detail=full&include_inactive=true&include_deleted=true',
      );
      return res.data.modules;
    },
  });

  const pricingQuery = useQuery({
    queryKey: ['admin', 'module-pricing', 'list'],
    queryFn: async () => {
      const res = await api.get<{ pricing: ModulePricingRow[] }>('/admin/module-pricing');
      return res.data.pricing;
    },
  });

  const [name, setName] = useState('');
  const [moduleCodes, setModuleCodes] = useState<string[]>([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Załaduj stan z pakietu raz po fetchu.
  useEffect(() => {
    if (bundleQuery.data) {
      setName(bundleQuery.data.name ?? '');
      setModuleCodes(bundleQuery.data.moduleCodes);
      setDiscountPercent(bundleQuery.data.discountPercent);
    }
  }, [bundleQuery.data]);

  const modulesByCode = useMemo(() => {
    const m = new Map<string, ModuleListItem>();
    (modulesQuery.data ?? []).forEach((mod) => m.set(mod.code, mod));
    return m;
  }, [modulesQuery.data]);

  const pricingByCode = useMemo(() => {
    const m = new Map<string, ModulePricingRow>();
    (pricingQuery.data ?? [])
      .filter((p) => p.currencyCode === 'PLN')
      .forEach((p) => m.set(p.moduleCode, p));
    return m;
  }, [pricingQuery.data]);

  // Skład + podsumowanie liczone na żywo (odzwierciedla niezapisane edycje).
  const composition = useMemo(() => {
    const items = moduleCodes.map((c) => {
      const mod = modulesByCode.get(c);
      const p = pricingByCode.get(c);
      return {
        code: c,
        name: mod?.name ?? c,
        retired: !!mod?.gcrecord,
        priceMonthly: p?.priceMonthly ?? null,
        priceYearly: p?.priceYearly ?? null,
      };
    });
    let sumMonthly = 0;
    let sumYearly = 0;
    let plnComplete = true;
    let yearlyComplete = true;
    for (const it of items) {
      if (it.priceMonthly === null) {
        plnComplete = false;
        continue;
      }
      sumMonthly += it.priceMonthly;
      if (it.priceYearly === null) yearlyComplete = false;
      else sumYearly += it.priceYearly;
    }
    const factor = (100 - discountPercent) / 100;
    return {
      items,
      sumMonthly: plnComplete ? sumMonthly : null,
      sumYearly: plnComplete && yearlyComplete ? sumYearly : null,
      bundleMonthly: plnComplete ? Math.round(sumMonthly * factor) : null,
      bundleYearly: plnComplete && yearlyComplete ? Math.round(sumYearly * factor) : null,
      savingMonthly: plnComplete ? Math.round(sumMonthly * (discountPercent / 100)) : null,
    };
  }, [moduleCodes, discountPercent, modulesByCode, pricingByCode]);

  const nameValid = name.trim().length > 0;
  const modulesValid = moduleCodes.length >= 2;
  const discountValid = discountPercent >= 0 && discountPercent <= 100;
  const bundle = bundleQuery.data;
  const dirty =
    !!bundle &&
    (name.trim() !== (bundle.name ?? '') ||
      JSON.stringify(moduleCodes) !== JSON.stringify(bundle.moduleCodes) ||
      discountPercent !== bundle.discountPercent);
  const canSave = nameValid && modulesValid && discountValid && dirty;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (name.trim() !== (bundle?.name ?? '')) patch.name = name.trim();
      if (JSON.stringify(moduleCodes) !== JSON.stringify(bundle?.moduleCodes))
        patch.moduleCodes = moduleCodes;
      if (discountPercent !== bundle?.discountPercent) patch.discountPercent = discountPercent;
      await api.patch(`/admin/bundles/${code}`, patch);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', description: t('admin.bundles.edit.savedToast') });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'bundles'] });
      navigate('/bundles');
    },
    onError: (err) => {
      const ax = err as AxiosError<{ error?: string }>;
      const c = ax.response?.data?.error;
      setError(
        c === 'MODULE_RETIRED'
          ? t('admin.bundles.modal.errors.moduleRetired')
          : c === 'VALIDATION_FAILED'
            ? t('admin.bundles.modal.errors.validation')
            : t('admin.bundles.modal.errors.generic'),
      );
    },
  });

  useEffect(() => setError(null), [name, moduleCodes, discountPercent]);

  const toggleModule = (c: string) =>
    setModuleCodes((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const moduleOptions = (modulesQuery.data ?? []).filter(
    (m) => !m.gcrecord || moduleCodes.includes(m.code),
  );

  if (bundleQuery.isError) {
    return (
      <Layout title={t('admin.bundles.edit.title')}>
        <section className="mx-auto max-w-5xl px-6 py-8">
          <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {t('admin.bundles.edit.loadError')}
            <Button
              variant="secondary"
              size="sm"
              className="ml-3"
              onClick={() => navigate('/bundles')}
            >
              {t('admin.bundles.edit.backToList')}
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout title={t('admin.bundles.edit.title')}>
      <section className="mx-auto max-w-5xl px-6 py-8 flex flex-col gap-6">
        {/* Nagłówek */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/bundles')}
              aria-label={t('admin.bundles.edit.backToList')}
              className="rounded p-1.5 text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">
                {bundleQuery.isLoading ? '…' : name || bundle?.code}
              </h1>
              {bundle && (
                <div className="mt-0.5 flex items-center gap-2 text-sm text-text-tertiary">
                  <code>{bundle.code}</code>
                  <Badge color={bundle.isActive ? 'green' : 'gray'}>
                    {t(
                      bundle.isActive ? 'admin.bundles.edit.active' : 'admin.bundles.edit.inactive',
                    )}
                  </Badge>
                  {bundle.gcrecord && (
                    <Badge color="amber">{t('admin.bundles.edit.deleted')}</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!canSave || saveMutation.isPending}
            data-testid="bundle-edit-save"
          >
            {t('admin.bundles.edit.save')}
          </Button>
        </div>

        {bundleQuery.isLoading ? (
          <Skeleton height={320} />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Formularz */}
            <div className="flex flex-col gap-4">
              <Input
                label={t('admin.bundles.modal.nameLabel')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={
                  !nameValid && name.length > 0 ? t('admin.bundles.edit.nameRequired') : undefined
                }
              />
              <Input
                label={t('admin.bundles.modal.discountLabel')}
                type="number"
                min={0}
                max={100}
                value={String(discountPercent)}
                onChange={(e) => setDiscountPercent(Number(e.target.value))}
              />
              <fieldset className="flex flex-col gap-2 text-sm">
                <legend className="font-medium text-text-secondary">
                  {t('admin.bundles.modal.modulesLabel')}
                </legend>
                {modulesQuery.isLoading && (
                  <p className="text-xs text-text-muted">
                    {t('admin.bundles.modal.modulesLoading')}
                  </p>
                )}
                {moduleOptions.map((m) => {
                  const p = pricingByCode.get(m.code);
                  return (
                    <Checkbox
                      key={m.code}
                      label={
                        <span>
                          <span className="font-mono text-xs">{m.code}</span> — {m.name}
                          {p && (
                            <span className="ml-2 text-xs text-text-muted">
                              {formatPln(p.priceMonthly)} PLN/mies
                            </span>
                          )}
                          {m.gcrecord && (
                            <span className="ml-2 text-xs text-amber-600">
                              {t('admin.bundles.retiredBadge')}
                            </span>
                          )}
                        </span>
                      }
                      checked={moduleCodes.includes(m.code)}
                      onChange={() => toggleModule(m.code)}
                    />
                  );
                })}
                {!modulesValid && (
                  <span className="text-xs text-red-600">
                    {t('admin.bundles.modal.modulesMin2')}
                  </span>
                )}
              </fieldset>
            </div>

            {/* Skład pakietu */}
            <div className="flex flex-col gap-3 rounded-xl border border-border-secondary bg-bg-elevated p-5">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-text-tertiary" />
                <h2 className="text-sm font-semibold text-text-primary">
                  {t('admin.bundles.edit.compositionTitle', { count: composition.items.length })}
                </h2>
              </div>

              {composition.items.length === 0 ? (
                <p className="text-xs text-text-muted">
                  {t('admin.bundles.edit.compositionEmpty')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-secondary text-left text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                        <th className="py-1.5 pr-3">{t('admin.bundles.edit.colModule')}</th>
                        <th className="py-1.5 pr-3">{t('admin.bundles.edit.colCode')}</th>
                        <th className="py-1.5 text-right">{t('admin.bundles.edit.colPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-secondary">
                      {composition.items.map((it) => (
                        <tr key={it.code} data-testid={`bundle-module-${it.code}`}>
                          <td className="max-w-0 truncate py-2 pr-3 text-text-primary">
                            {it.name}
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs text-text-tertiary">
                            {it.code}
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {it.retired && (
                                <Badge color="amber">{t('admin.bundles.retiredBadge')}</Badge>
                              )}
                              {it.priceMonthly === null ? (
                                <Badge color="red">{t('admin.bundles.brokenPlnBadge')}</Badge>
                              ) : (
                                <span className="whitespace-nowrap text-xs text-text-secondary">
                                  {formatPln(it.priceMonthly)} / {formatPln(it.priceYearly)}
                                  <span className="ml-1 text-text-muted">PLN m/r</span>
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Podsumowanie cen */}
              <div className="mt-2 flex flex-col gap-1.5 border-t border-border-secondary pt-3 text-sm">
                <div className="flex justify-between text-text-secondary">
                  <span>{t('admin.bundles.edit.sumLabel')}</span>
                  <span className="font-mono">
                    {formatPln(composition.sumMonthly)} / {formatPln(composition.sumYearly)}{' '}
                    <span className="text-text-muted">PLN m/r</span>
                  </span>
                </div>
                <div className="flex justify-between text-text-secondary">
                  <span>{t('admin.bundles.modal.discountLabel')}</span>
                  <span className="font-mono">{discountPercent}%</span>
                </div>
                <div className="flex justify-between font-medium text-text-primary">
                  <span>{t('admin.bundles.edit.bundlePriceLabel')}</span>
                  <span className="font-mono">
                    {formatPln(composition.bundleMonthly)} / {formatPln(composition.bundleYearly)}{' '}
                    <span className="text-text-muted">PLN m/r</span>
                  </span>
                </div>
                {composition.savingMonthly !== null && composition.savingMonthly > 0 && (
                  <div className="flex justify-between text-green-700 dark:text-green-400">
                    <span className="flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" />
                      {t('admin.bundles.edit.savingLabel')}
                    </span>
                    <span className="font-mono">
                      {formatPln(composition.savingMonthly)} PLN/mies ({discountPercent}%)
                    </span>
                  </div>
                )}
              </div>

              <p className="mt-1 text-xs text-text-muted">{t('admin.bundles.previewBanner')}</p>
            </div>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </section>
    </Layout>
  );
}
